// The four-clinic overview strip — one My Desk panel type (`clinicStrip`,
// N-090). Extracted verbatim from Home.tsx (N-089/N-090): a manager sees
// only their own clinic's tile, owner/director see all four, canonical
// order always (INV-14). Markup and classnames are UNCHANGED from before
// the panel-catalog rewrite — `summarise` is still a plain, independently
// tested function (client/tests/home.test.tsx's `describe('summarise', ...)`
// imports it from here now).
//
// Ground truth: ~/nvManager-files/workspace/board/Code.gs
// buildLocationProgressCards_ / boardCss_ (.locations/.loc/.nm/.big/.track/
// .foot) — one card per clinic.
import { currentTaskList } from '../lib/bootstrap';
import type { JSX } from 'preact';
import { state } from '../lib/store';
import { LOCATION_ORDER, LOCATION_NAMES, type LocationCode } from '../lib/locations';
import { visibleClinics } from './ClinicTabs';
import { displayStatus, type TaskRow } from '../pages/Tasks';

interface WorkItemRow {
  id: number;
  locationCode: string | null;
  status: string;
  agingDays: number;
  assignee: string | null;
}

/** Statuses that mean "someone still has to do something about this". */
const NEEDS_ATTENTION = new Set(['OVERDUE', 'SENT_BACK', 'BLOCKED']);

export interface ClinicSummary {
  code: LocationCode;
  name: string;
  total: number;
  done: number;
  needsAttention: number;
  openWorkItems: number;
  oldestWorkItemDays: number | null;
}

/**
 * OWNER RULING, 2026-08-15: "there is no need for clinic hues, they should
 * just be the same as brand purple — a manager only sees their page." The
 * `location.hue` column and `--color-clinic-*` tokens stay unused, not
 * re-derived — the data is not wrong, it is just not drawn.
 */
function markColour(): string {
  return 'var(--color-brand-purple)';
}

/** One row per clinic the viewer may SEE, in canonical order (INV-14) —
 * never per-page ordering. `clinics` is the viewer's own set
 * (`visibleClinics()`). */
export function summarise(
  tasks: TaskRow[],
  workItems: WorkItemRow[],
  now: Date,
  clinics: readonly LocationCode[] = LOCATION_ORDER
): ClinicSummary[] {
  return LOCATION_ORDER.filter((code) => clinics.includes(code)).map((code) => {
    const mine = tasks.filter((t) => t.location_code === code);
    const items = workItems.filter((w) => w.locationCode === code && w.status !== 'RESOLVED' && w.status !== 'DROPPED');
    const ages = items.map((w) => w.agingDays).filter((n) => Number.isFinite(n));
    return {
      code,
      name: LOCATION_NAMES[code],
      total: mine.length,
      done: mine.filter((t) => ['DONE', 'VERIFIED'].includes(displayStatus(t, now))).length,
      needsAttention: mine.filter((t) => NEEDS_ATTENTION.has(displayStatus(t, now))).length,
      openWorkItems: items.length,
      oldestWorkItemDays: ages.length ? Math.max(...ages) : null,
    };
  });
}

/** One row of the one-clinic Tasks table (owner, 2026-10-12): a task list
 * the clinic has this period — Daily, Weekly, Monthly, Manager. */
export interface TaskListSummary {
  cadence: TaskRow['cadence'];
  label: string;
  done: number;
  left: number;
  /** Anything overdue, sent back or blocked on this list. */
  attention: number;
}

/** The task lists in the Tasks page's own order (pages/Tasks.tsx
 * CADENCE_ORDER / CADENCE_LABEL). */
const LIST_ORDER: { cadence: TaskRow['cadence']; label: string }[] = [
  { cadence: 'DAILY', label: 'Daily' },
  { cadence: 'WEEKLY', label: 'Weekly' },
  { cadence: 'MONTHLY', label: 'Monthly' },
  { cadence: 'MANAGER', label: 'Manager' },
];

/** One row per task list `code` has in `tasks`, counted from derived status
 * (OVERDUE is never stored — displayStatus). A list with no rows is left
 * out; it is not a list this clinic has this period. Pure. */
export function summariseLists(tasks: TaskRow[], code: string, now: Date): TaskListSummary[] {
  const mine = tasks.filter((t) => t.location_code === code);
  return LIST_ORDER.flatMap(({ cadence, label }) => {
    const rows = mine.filter((t) => t.cadence === cadence);
    if (!rows.length) return [];
    const statuses = rows.map((t) => displayStatus(t, now));
    const done = statuses.filter((st) => st === 'DONE' || st === 'VERIFIED').length;
    return [{
      cadence,
      label,
      done,
      left: rows.length - done,
      attention: statuses.filter((st) => NEEDS_ATTENTION.has(st)).length,
    }];
  });
}

/** The one-clinic Tasks box (owner, 2026-10-12: "Make the Tasks a table that
 * is a 1/4 page box"): a compact table — list, Done, Left — and a total
 * line. No big number, no progress bar, no clinic name. A row with anything
 * overdue or sent back is in the warn colour. */
function TaskListTable({ code, tasks, now }: { code: string; tasks: TaskRow[]; now: Date }): JSX.Element {
  const lists = summariseLists(tasks, code, now);
  if (!lists.length) {
    return <p class="muted desk-task-empty" data-testid="home-overview">No tasks this period.</p>;
  }
  const done = lists.reduce((n, l) => n + l.done, 0);
  const left = lists.reduce((n, l) => n + l.left, 0);
  const attention = lists.reduce((n, l) => n + l.attention, 0);
  return (
    <table class="table desk-task-table" data-testid="home-overview">
      <thead>
        <tr><th>List</th><th>Done</th><th>Left</th></tr>
      </thead>
      <tbody>
        {lists.map((l) => (
          <tr
            key={l.cadence}
            class={l.attention ? 'is-warn' : undefined}
            data-testid={`task-list-${l.cadence}`}
            title={l.attention ? `${l.attention} overdue or sent back` : undefined}
          >
            <td><a href="/tasks">{l.label}</a></td>
            <td>{l.done}</td>
            <td>{l.left}</td>
          </tr>
        ))}
        {lists.length > 1 ? (
          <tr class={`desk-task-total${attention ? ' is-warn' : ''}`} data-testid="task-list-total">
            <td>Total</td>
            <td>{done}</td>
            <td>{left}</td>
          </tr>
        ) : null}
      </tbody>
    </table>
  );
}

export function ClinicOverviewStrip(): JSX.Element | null {
  const now = new Date();
  const tasks = currentTaskList<TaskRow>();
  const workItems = Object.values((state.value.workItem ?? {}) as Record<string, WorkItemRow>);
  const clinics = visibleClinics();

  if (!tasks.length && !workItems.length) return null;

  // Owner, 2026-10-12: a one-clinic viewer (a manager, or the owner viewing
  // as one clinic) gets a small "Tasks" table — the panel title is "Tasks"
  // (panelCatalog.tsx labelFor), and the clinic's name is NOT repeated
  // inside it; it is their clinic.
  if (clinics.length === 1) return <TaskListTable code={clinics[0]} tasks={tasks} now={now} />;

  const rows = summarise(tasks, workItems, now, clinics);
  return (
    <div class="locations" data-testid="home-overview">
      {rows.map((r) => {
        const pct = r.total ? Math.round((r.done / r.total) * 100) : 0;
        return (
          <div class="loc" key={r.code} data-attention={r.needsAttention > 0 ? 'true' : 'false'}>
            <div class="nm">
              <i style={{ background: markColour() }} />
              {r.name}
            </div>
            <div class="big">
              {r.done}
              <s>/{r.total}</s>
            </div>
            <div class="track">
              <i style={{ width: `${pct}%`, background: markColour() }} />
            </div>
            <div class="foot">
              {r.total ? `${r.total - r.done} remaining` : 'No tasks this period'}
              {r.openWorkItems ? ` · ${r.openWorkItems} open work item${r.openWorkItems === 1 ? '' : 's'}` : ''}
            </div>
          </div>
        );
      })}
    </div>
  );
}
